import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Loader2, Save, Info, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as API } from '../../config/api';

const COMMON_PATTERNS = ['V', 'CV', 'VC', 'CVC', 'CCV', 'CCVC', 'CVCC', 'CCVCC'];

const PhonotacticsPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [syllablePatterns, setSyllablePatterns] = useState([]);
  const [onsetConstraints, setOnsetConstraints] = useState('');
  const [codaConstraints, setCodaConstraints] = useState('');
  const [notes, setNotes] = useState('');
  const [newPattern, setNewPattern] = useState('');

  useEffect(() => {
    fetchData();
  }, [conlangId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/phonotactics`, { withCredentials: true });
      if (res.data) {
        setSyllablePatterns(res.data.syllable_patterns || []);
        setOnsetConstraints(res.data.onset_constraints || '');
        setCodaConstraints(res.data.coda_constraints || '');
        setNotes(res.data.notes || '');
      }
    } catch (error) {
      if (error.response?.status !== 404) toast.error('Failed to load phonotactics');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/conlangs/${conlangId}/phonotactics`, {
        syllable_patterns: syllablePatterns,
        allowed_onsets: [],
        allowed_codas: [],
        allowed_nuclei: [],
        onset_constraints: onsetConstraints || null,
        coda_constraints: codaConstraints || null,
        notes: notes || null,
      }, { withCredentials: true });
      toast.success('Phonotactics saved');
    } catch (error) {
      toast.error('Failed to save phonotactics');
    } finally {
      setSaving(false);
    }
  };

  const addPattern = (pattern) => {
    if (pattern && !syllablePatterns.includes(pattern)) {
      setSyllablePatterns([...syllablePatterns, pattern]);
    }
    setNewPattern('');
  };

  const removePattern = (pattern) => {
    setSyllablePatterns(syllablePatterns.filter(p => p !== pattern));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="phonotactics-title">Phonotactics</h2>
          <p className="text-foreground-muted mt-1">Define syllable structure and sound distribution rules for {conlang?.name}</p>
        </div>
        {canEdit && (
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-phonotactics-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
        )}
      </div>

      {/* Syllable Patterns */}
      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Syllable Patterns</CardTitle>
          <CardDescription>C = consonant, V = vowel. Select common patterns or add custom ones.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {COMMON_PATTERNS.map(p => (
              <Button
                key={p}
                variant={syllablePatterns.includes(p) ? 'default' : 'outline'}
                size="sm"
                onClick={() => syllablePatterns.includes(p) ? removePattern(p) : addPattern(p)}
                className={syllablePatterns.includes(p) ? 'bg-primary hover:bg-primary/90' : 'border-border'}
                data-testid={`pattern-${p}`}
              >
                {p}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newPattern}
              onChange={(e) => setNewPattern(e.target.value.toUpperCase())}
              placeholder="Custom pattern (e.g., CCCV)"
              className="bg-background border-border font-mono max-w-xs"
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPattern(newPattern))}
              data-testid="custom-pattern-input"
            />
            <Button variant="outline" size="sm" onClick={() => addPattern(newPattern)} disabled={!newPattern} className="border-border">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {syllablePatterns.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="selected-patterns">
              {syllablePatterns.map(p => (
                <Badge key={p} variant="secondary" className="bg-primary/20 text-primary gap-1 font-mono">
                  {p}
                  <button onClick={() => removePattern(p)} className="hover:text-destructive"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Onset Constraints */}
      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Onset Constraints</CardTitle>
          <CardDescription>Rules for which consonants or clusters can begin a syllable</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={onsetConstraints}
            onChange={(e) => setOnsetConstraints(e.target.value)}
            placeholder="Describe which consonant clusters are allowed at the beginning of syllables, any restrictions on combinations..."
            className="bg-background border-border min-h-[120px]"
            data-testid="onset-constraints"
          />
        </CardContent>
      </Card>

      {/* Coda Constraints */}
      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Coda Constraints</CardTitle>
          <CardDescription>Rules for which consonants or clusters can end a syllable</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={codaConstraints}
            onChange={(e) => setCodaConstraints(e.target.value)}
            placeholder="Describe which consonants are allowed at the end of syllables, whether codas are optional, any restrictions..."
            className="bg-background border-border min-h-[120px]"
            data-testid="coda-constraints"
          />
        </CardContent>
      </Card>

      {/* General Notes */}
      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Additional Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional phonotactic rules, vowel harmony, consonant harmony, other distributional patterns..."
            className="bg-background border-border min-h-[120px]"
            data-testid="phonotactics-notes"
          />
        </CardContent>
      </Card>

      <div className="p-4 rounded-md bg-secondary/10 border border-secondary/30 flex gap-3">
        <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
        <div className="text-secondary text-sm">
          <strong>Phonotactics</strong> describes the rules for how sounds combine in your language. This includes which consonant clusters are allowed, syllable structure, and any restrictions on sound sequences.
        </div>
      </div>
    </div>
  );
};

export default PhonotacticsPage;
